// Content for the /​<slug> SEO landing pages, one object per page.
//
// Why a data file instead of 10 page components: every page below shares the
// exact same layout (hero → sections → FAQ → related → CTA), rendered by
// LandingPage.js. Splitting layout from content is what makes this scalable —
// shipping an 11th page later is "add an object here + one route", not "copy
// a 300-line JSX file and hope nothing drifts out of sync with the design."
//
// Content rules that keep these from reading as 10 spun copies of one page:
// - each page targets a different search intent (format, moment, or feeling),
//   see the one-line "angle" comment above each entry
// - no two pages share a heading, FAQ question, or hero image + alt pairing
// - the five wellness-cluster pages (emotional-support, mental-wellness,
//   relationship-advice, stress-relief, loneliness-help) carry a disclaimer —
//   Baatcheet is peer conversation, not licensed therapy, and the copy never
//   claims otherwise.

const landingData = {

  // Angle: privacy/anonymity mechanics — for people who want to talk without
  // revealing who they are. Format-neutral (works for both audio and text).
  'anonymous-chat-app': {
    slug: 'anonymous-chat-app',
    breadcrumbLabel: 'Anonymous Chat App',
    title: 'Anonymous Chat App — Talk Freely, No Profile Needed',
    description: 'Chat and voice call anonymously on Baatcheet — no phone number or photo shown to anyone. Real people, zero pressure. Free to start.',
    kicker: 'No name. No photo. No pressure.',
    h1: <>Talk Freely.<br /><span className="stamp">Stay Anonymous.</span></>,
    heroLead: "Most chat apps ask for a profile picture before you've said a word. Baatcheet doesn't. You pick a mood, you're matched, and you talk — your number, your face and your name stay yours until you decide otherwise.",
    heroImage: { src: '/safety-privacy.webp', alt: 'Baatcheet\'s end-to-end encrypted, no-profile-required chat design', w: 1536, h: 1024, label: 'Privacy by default' },
    sections: [
      {
        heading: 'Anonymous doesn\'t mean unsafe',
        paragraphs: [
          'On most apps, "anonymous" is a loophole people abuse. On Baatcheet it\'s the opposite: every single person behind an anonymous handle has already passed identity verification before their first message. You\'re anonymous to the other person — never to us, and never to accountability.',
          'That\'s the difference between anonymous and unaccountable. You get the freedom to speak without your name attached to it, without opening the door to bots, catfishing, or the kind of accounts that vanish the moment something goes wrong.',
        ],
        bullets: [
          'No phone number or photo shown to your match, ever',
          'Verified-human accounts only — checked before anyone can chat',
          'One-tap block and report if a conversation goes somewhere you don\'t want',
        ],
      },
      {
        heading: 'Reveal on your own terms',
        paragraphs: [
          'Some conversations are better left anonymous forever — a 2 AM vent, a rant about your day, a rehearsal for something you\'re nervous to say out loud. Others turn into something you want to carry forward.',
          'Baatcheet doesn\'t force either outcome. Stay anonymous for one call or fifty. If a connection is worth keeping, revealing more about yourself is always something you choose, not something the app decides for you.',
        ],
      },
    ],
    quote: { text: 'Pehli baar laga koi bina naam jaane bhi seedha samajh sakta hai.', attribution: 'A Baatcheet user, on their first anonymous call' },
    faqs: [
      { q: 'Do I need to share my phone number or photo to chat anonymously?', a: 'No. Your phone number is used only for OTP login and is never shown to anyone you talk to. There\'s no requirement to add a profile photo, and most people never do.' },
      { q: 'Can the other person find out who I am?', a: 'Not unless you tell them. Baatcheet doesn\'t share your number, last name, or any identifying details with the person you\'re matched with — the conversation itself is the only thing exchanged.' },
      { q: 'Is anonymous chatting on Baatcheet actually safe?', a: 'Yes — anonymity and safety aren\'t opposites here. Everyone on Baatcheet passes identity verification before they can chat with anyone, so you\'re anonymous to your match but never to the platform. Read more on our Safety page.' },
      { q: 'Can I reveal my identity later if I want to?', a: 'Yes, entirely your call. Plenty of Baatcheet friendships start anonymous and stay that way for months before anyone shares more — there\'s no pressure or built-in nudge to reveal anything.' },
    ],
    related: [
      { path: '/voice-chat-app', label: 'Voice Chat App' },
      { path: '/talk-to-someone', label: 'Talk to Someone' },
      { path: '/safety', label: 'How Baatcheet keeps you safe' },
    ],
    ctaHeading: <>Stay <span className="stamp">anonymous.</span></>,
    ctaBody: 'Download Baatcheet free and have your first anonymous conversation in under a minute — no profile required.',
  },

  // Angle: format — 1-on-1 live voice calling as an alternative to texting.
  'voice-chat-app': {
    slug: 'voice-chat-app',
    breadcrumbLabel: 'Voice Chat App',
    title: 'Voice Chat App — Real Voice Calls, Real People',
    description: 'Skip the texting. Baatcheet is a voice chat app that connects you to real people over live audio in under 8 seconds — free, anonymous-first.',
    kicker: 'Skip the typing. Just talk.',
    h1: <>Real Voice.<br /><span className="stamp">Real People.</span></>,
    heroLead: 'Texting flattens everything — sarcasm, tone, how someone actually feels. Baatcheet is built around one idea: a live voice does more in ten seconds than a paragraph of text ever will. Pick a mood, get matched, start talking.',
    heroImage: { src: '/how-call-screen.webp', alt: 'A live one-on-one voice call in progress on Baatcheet', w: 1536, h: 1024, label: 'Live · HD audio' },
    sections: [
      {
        heading: 'Why voice beats text',
        paragraphs: [
          'A voice note or a live call carries laughter, hesitation, the exact weight behind "I\'m fine" — all the things a text thread quietly strips out. That\'s the whole premise of a voice chat app: you\'re not managing a conversation through a keyboard, you\'re actually having one.',
          'Baatcheet\'s matching runs on mood, not swiping through photos. Tell it you want to chill, vent, or just talk about your day, and it finds someone on the same wavelength — usually inside 8 seconds.',
        ],
        bullets: [
          'HD audio calls, matched by mood in under 8 seconds',
          'No typing required — voice notes if you\'d rather not go live',
          'Leave any call anytime, no explanation needed',
        ],
      },
      {
        heading: 'Built for the moments texting can\'t handle',
        paragraphs: [
          'The commute home, the 20 minutes before sleep, the walk when you just need noise that isn\'t a podcast — these are the moments a voice chat app actually fits into. You don\'t have to compose a message or wait for a reply; you just talk, live, to someone who\'s also there for exactly that.',
        ],
      },
    ],
    quote: { text: 'Type karne ki himmat nahi thi, but bolna aasaan tha.', attribution: 'A Baatcheet user on their first voice call' },
    faqs: [
      { q: 'How is a voice chat app different from a regular calling app?', a: 'A regular calling app needs you to already have the other person\'s number and a reason to call. Baatcheet matches you with someone new, by mood, right when you open it — no contact list required.' },
      { q: 'Do I need the other person\'s phone number to voice chat?', a: 'No. Matching, calling, and the entire conversation happen inside the app — numbers are never exchanged unless both people choose to share them.' },
      { q: 'What happens if I don\'t like the call — can I leave anytime?', a: 'Yes, instantly, with one tap and no explanation required. You can also block and report from the same screen if needed.' },
      { q: 'Does voice chat use a lot of mobile data?', a: 'Baatcheet\'s calls are optimized for low-bandwidth Indian mobile networks — a typical call uses roughly what a voice note-heavy WhatsApp chat would.' },
    ],
    related: [
      { path: '/audio-chat-app', label: 'Audio Chat App (group rooms)' },
      { path: '/anonymous-chat-app', label: 'Anonymous Chat App' },
      { path: '/how-it-works', label: 'How Baatcheet works' },
    ],
    ctaHeading: <>Hear a <span className="stamp">real voice.</span></>,
    ctaBody: 'Download Baatcheet free and get matched for your first live voice call in under 8 seconds.',
  },

  // Angle: format — GROUP audio rooms (many people, live), distinct from
  // voice-chat-app's 1-on-1 framing above.
  'audio-chat-app': {
    slug: 'audio-chat-app',
    breadcrumbLabel: 'Audio Chat App',
    title: 'Audio Chat App — Join Live Group Voice Rooms',
    description: 'Hop into live audio rooms on Baatcheet — group voice chats on music, cricket, late-night talk and more. Free to join, anytime.',
    kicker: 'One room. Many voices.',
    h1: <>Jump Into a<br /><span className="stamp">Live Audio Room.</span></>,
    heroLead: 'Sometimes you don\'t want a one-on-one call — you want to drop into a room where a conversation is already happening. Baatcheet\'s live audio rooms (we call them mehfils) are always running: late-night confessions, cricket chatter, shaayri, music.',
    heroImage: { src: '/group-call.webp', alt: 'Several people together in a live Baatcheet audio room', w: 1402, h: 1122, label: 'Live now · mehfil' },
    sections: [
      {
        heading: 'A room for every mood',
        paragraphs: [
          'A 1-on-1 call is a conversation with one other person. An audio room is a hangout — you can jump in, listen for a while, raise your hand to speak, or just sit in the back and vibe with the room\'s energy. Rooms run on themes, not a fixed schedule: late-night confessions, cricket-ke-baad-ki-chai, shaayri ki raat, and dozens more happening at any given hour.',
          'It\'s the closest thing to walking into a real adda mid-conversation — except you can leave whenever you want, no goodbyes required.',
        ],
        bullets: [
          'Dozens of themed rooms live at any time — no scheduling needed',
          'Listen silently or raise your hand to join in',
          'Every room is moderated, with instant mute/report tools for hosts',
        ],
      },
      {
        heading: 'Low pressure, real energy',
        paragraphs: [
          'Group audio takes the pressure off a one-on-one call. You don\'t have to carry the conversation — you can lurk for your first few visits and just enjoy other people\'s voices, then start speaking up whenever you\'re ready. A lot of Baatcheet\'s regular users started exactly this way.',
        ],
      },
    ],
    faqs: [
      { q: 'What\'s the difference between a 1-on-1 call and an audio room?', a: 'A 1-on-1 call matches you privately with one person. An audio room is a live group hangout anyone can join, listen to, or speak in — more like walking into a conversation than starting one.' },
      { q: 'Can I just listen in an audio room without speaking?', a: 'Yes. You can join any room silently and only raise your hand to speak when — or if — you feel like it. No pressure to talk.' },
      { q: 'How many people can join one audio room?', a: 'Rooms are built for group hangouts, typically ranging from a handful of people up to a few dozen depending on how popular the topic is that night.' },
      { q: 'Are audio rooms moderated?', a: 'Yes. Every room has moderation tools — hosts can mute or remove disruptive users instantly, and every room carries the same one-tap report option as 1-on-1 calls.' },
    ],
    related: [
      { path: '/voice-chat-app', label: 'Voice Chat App (1-on-1)' },
      { path: '/online-friends', label: 'Make Online Friends' },
      { path: '/how-it-works', label: 'How Baatcheet works' },
    ],
    ctaHeading: <>Join a <span className="stamp">live room.</span></>,
    ctaBody: 'Download Baatcheet free and drop into a live audio room tonight — no invite needed.',
  },

  // Angle: intent-based, immediate — "I need to talk to someone right now."
  // Reactive/in-the-moment, not about ongoing relationships.
  'talk-to-someone': {
    slug: 'talk-to-someone',
    breadcrumbLabel: 'Talk to Someone',
    title: 'Talk to Someone Right Now — Free, Anonymous, 24/7',
    description: 'Need to talk to someone right now? Baatcheet matches you with a real person in under 8 seconds — anytime, day or night, free to start.',
    kicker: 'Right now. No appointment.',
    h1: <>Someone's Always<br /><span className="stamp">There to Talk.</span></>,
    heroLead: 'Not every "I need to talk to someone" moment can wait for a scheduled call with a friend who\'s asleep or busy. Baatcheet exists for exactly that gap — open the app, pick a mood, and you\'re talking to a real, verified person in under 8 seconds.',
    heroImage: { src: '/how-app-dark.webp', alt: 'Baatcheet\'s instant mood-matched call screen, ready in seconds', w: 1349, h: 1166, label: '8-second match' },
    sections: [
      {
        heading: 'No waiting, no scheduling',
        paragraphs: [
          'There\'s always someone awake on Baatcheet — it\'s built for the 2 AM moments as much as the 2 PM ones. You don\'t need to explain why you\'re reaching out or wait for someone to "have time." Set your mood, get matched, start talking.',
          'You don\'t owe anyone context either. Plenty of conversations on Baatcheet start with nothing more than "I just needed to talk to someone" — that\'s a complete enough reason.',
        ],
        bullets: [
          'Real, verified people online around the clock',
          'Matched in under 8 seconds — no queue, no waiting room',
          'You set the tone: vent, chill, or just talk about nothing in particular',
        ],
      },
      {
        heading: 'It\'s fine if you just want to vent',
        paragraphs: [
          'Not every conversation needs a takeaway or a solution. Sometimes talking to someone is the point, full stop — getting a thought out of your head and into words, out loud, to another person who\'s actually listening. Baatcheet doesn\'t push advice on you unless you want it.',
        ],
      },
    ],
    quote: { text: '2 baje neend nahi aati — yahan koi na koi jaag raha hota hai.', attribution: 'A Baatcheet user' },
    faqs: [
      { q: 'Is someone actually available to talk right now, even at 2 AM?', a: 'Yes — Baatcheet has real people online at every hour, which is exactly why late-night is one of the busiest times on the app.' },
      { q: 'Do I have to explain why I want to talk?', a: 'No. You never need a reason or backstory before starting a call. Plenty of people open with nothing more than "just wanted to talk."' },
      { q: 'What if I just want to vent and not get advice?', a: 'That\'s completely fine and common. You can say upfront that you just want to be heard — most people on Baatcheet are there to listen, not to fix.' },
      { q: 'Is this free the first time?', a: 'Yes, your first call on Baatcheet is free, always. The app itself is also free to download on Android.' },
    ],
    related: [
      { path: '/emotional-support', label: 'Emotional Support' },
      { path: '/loneliness-help', label: 'Loneliness Help' },
      { path: '/download', label: 'Download Baatcheet' },
    ],
    ctaHeading: <>Talk to <span className="stamp">someone now.</span></>,
    ctaBody: 'Download Baatcheet free — your first call is on the house, day or night.',
  },

  // Angle: reactive emotional support — venting, being heard, non-judgment.
  // Wellness cluster — carries the peer-support disclaimer.
  'emotional-support': {
    slug: 'emotional-support',
    breadcrumbLabel: 'Emotional Support',
    title: 'Emotional Support — Someone to Talk To, Judgment-Free',
    description: 'Get emotional support from real people on Baatcheet. Talk through what\'s on your mind, anonymously, without being judged. Free to start.',
    kicker: 'You don\'t have to explain everything.',
    h1: <>You Don't Have to<br /><span className="stamp">Carry It Alone.</span></>,
    heroLead: 'A bad day, a breakup, a fight with someone you love, a week that just didn\'t go right — some of it doesn\'t need advice, it needs a person on the other end who\'s actually listening. That\'s what Baatcheet is built for.',
    heroImage: { src: '/how-couple.webp', alt: 'Two friends in a warm, supportive conversation', w: 1536, h: 1024, label: 'A real conversation' },
    sections: [
      {
        heading: 'A real person, not a script',
        paragraphs: [
          'Baatcheet matches you with a real, identity-verified human — not a bot, not a canned response. You can talk through whatever\'s weighing on you: work stress, a falling-out with a friend, family pressure, or nothing specific at all, just a heavy day. The other person\'s only job is to listen.',
          'Because it\'s anonymous by default, there\'s no history to manage and no fear of it coming up again with someone you\'ll see tomorrow. You can be as open as you want with someone who has no other context about your life.',
        ],
        bullets: [
          'Verified real people, available any time of day',
          'Anonymous — nothing you share is tied to your name or number',
          'No script, no forced advice — just someone listening',
        ],
      },
      {
        heading: 'Come back to the same conversation, or start fresh',
        paragraphs: [
          'If a conversation helped, you\'re free to talk to the same person again. If you\'d rather start over with someone new each time, that\'s just as easy. There\'s no single "right way" to use emotional support on Baatcheet — only what actually helps you that day.',
        ],
      },
    ],
    disclaimer: 'Baatcheet connects you with real people for peer conversation and emotional support — it isn\'t a substitute for therapy or professional mental health care. If you\'re in crisis or need clinical support, please reach out to a licensed mental health professional or a local helpline.',
    faqs: [
      { q: 'Is this the same as therapy or counseling?', a: 'No — Baatcheet is peer conversation with real, verified people, not licensed therapy. If you need clinical support, please reach out to a mental health professional or helpline.' },
      { q: 'Will the person judge me for what I share?', a: 'Baatcheet is built around anonymous, no-judgment conversation, and every user agrees to community guidelines around respectful listening. If a conversation ever feels judgmental, you can block and report instantly.' },
      { q: 'What if I get emotional or cry during the call?', a: 'That\'s completely fine — it happens often. You\'re talking to another person, not performing for one, and you can end the call whenever you need to.' },
      { q: 'Can I talk about the same thing again with someone new?', a: 'Yes. There\'s no limit on how many times you revisit a topic, whether with the same person or someone new each time.' },
    ],
    related: [
      { path: '/talk-to-someone', label: 'Talk to Someone Right Now' },
      { path: '/stress-relief', label: 'Stress Relief' },
      { path: '/safety', label: 'How Baatcheet keeps you safe' },
    ],
    ctaHeading: <>You don't have to <span className="stamp">carry it alone.</span></>,
    ctaBody: 'Download Baatcheet free and talk to a real person whenever you need one.',
  },

  // Angle: proactive/routine wellness habit — distinct from emotional-support's
  // reactive framing.
  'mental-wellness': {
    slug: 'mental-wellness',
    breadcrumbLabel: 'Mental Wellness',
    title: 'Mental Wellness App — Talk It Out, Feel Lighter',
    description: 'Make voice conversation part of your wellness routine. Baatcheet helps you decompress, reflect and reconnect — one call at a time.',
    kicker: 'A habit, not just a fix.',
    h1: <>Make Talking Part<br />of Your <span className="stamp">Routine.</span></>,
    heroLead: 'Mental wellness isn\'t usually one big breakthrough — it\'s small habits, repeated. A short call to decompress after work. A voice note before bed instead of doom-scrolling. Baatcheet is built to slot into that kind of routine, not just show up in a crisis.',
    heroImage: { src: '/safety-community.webp', alt: 'Baatcheet\'s calm, supportive voice community', w: 1536, h: 1024, label: 'A daily habit' },
    sections: [
      {
        heading: 'Small, regular check-ins beat one big vent',
        paragraphs: [
          'A five-minute call after a long day, a voice note journaling how the week went, a live room in the background while you unwind — these small, repeatable habits do more for your headspace over time than waiting until things pile up. Baatcheet is designed around that rhythm: quick to open, quick to match, easy to make a habit of.',
        ],
        bullets: [
          'Voice notes for quiet, low-effort journaling',
          'Mood-based matching so a 5-minute call fits into any routine',
          'Live rooms to unwind in the background without a 1-on-1 commitment',
        ],
      },
      {
        heading: 'It fits around your day, not the other way round',
        paragraphs: [
          'You don\'t need to set aside a fixed hour or commit to a program. Open Baatcheet for two minutes between classes, during a commute, or right before sleep — the app is built for these small windows, not long scheduled sessions.',
        ],
      },
    ],
    disclaimer: 'Baatcheet is a peer conversation app meant to support everyday wellness habits — it isn\'t a substitute for therapy or clinical mental health treatment. If you\'re struggling significantly, please speak with a licensed professional.',
    faqs: [
      { q: 'How can talking on the phone actually help my mental wellness?', a: 'Putting thoughts into words, out loud, to another person is a well-known way to process stress and gain a bit of distance from it — Baatcheet just makes that easy to do on a regular basis, not only during a crisis.' },
      { q: 'Do I need to talk every day for it to help?', a: 'No. Some users check in daily, others a couple of times a week — there\'s no required schedule. Even occasional use fits the idea of a "wellness habit."' },
      { q: 'Can I use voice notes instead of a live call?', a: 'Yes. Voice notes work well for reflective, journaling-style check-ins when you don\'t want a live back-and-forth.' },
      { q: 'Is this a replacement for therapy?', a: 'No. Baatcheet supports everyday wellness through peer conversation, but it isn\'t clinical care. If you need that level of support, please speak with a licensed mental health professional.' },
    ],
    related: [
      { path: '/stress-relief', label: 'Stress Relief' },
      { path: '/emotional-support', label: 'Emotional Support' },
      { path: '/how-it-works', label: 'How Baatcheet works' },
    ],
    ctaHeading: <>Make it a <span className="stamp">habit.</span></>,
    ctaBody: 'Download Baatcheet free and fit a real conversation into your week.',
  },

  // Angle: relationship-specific outside perspective — romantic, friendship,
  // or family — distinct topic from the broader emotional-support page.
  'relationship-advice': {
    slug: 'relationship-advice',
    breadcrumbLabel: 'Relationship Advice',
    title: 'Relationship Advice — Talk It Through With Someone',
    description: 'Confused about a relationship? Talk it through anonymously with a real person on Baatcheet and get an honest outside perspective.',
    kicker: 'An outside view, no history involved.',
    h1: <>Talk It Through,<br /><span className="stamp">Judgment-Free.</span></>,
    heroLead: 'The friends closest to a relationship problem are often the least neutral about it — they know the people involved, they have opinions already formed. Sometimes the most useful voice is a complete stranger\'s: no history, no side already picked.',
    heroImage: { src: '/how-couple.webp', alt: 'Two people talking through something honestly', w: 1536, h: 1024, label: 'An honest outside view' },
    sections: [
      {
        heading: 'A stranger with no stake in the outcome',
        paragraphs: [
          'Talking to someone who has zero connection to your relationship — romantic, friendship, or family — means they have nothing to protect and no side to defend. You can lay out the whole messy version of events without worrying about it changing how they see your partner, your friend, or your family member next time they see them, because they never will.',
          'That distance often makes it easier to hear things plainly, and easier to say things out loud you\'ve been avoiding even thinking clearly about.',
        ],
        bullets: [
          'Anonymous — the person you talk to has no connection to anyone involved',
          'Works for romantic relationships, friendships, or family situations',
          'No pressure to "resolve" anything on one call',
        ],
      },
      {
        heading: 'Someone to listen, not to referee',
        paragraphs: [
          'Baatcheet isn\'t a place to get your side "confirmed" — it\'s a place to say the whole thing out loud and hear how it sounds. Sometimes that\'s enough to know what you actually think. Other times, just being heard without interruption is the useful part.',
        ],
      },
    ],
    disclaimer: 'Conversations on Baatcheet are peer perspective from another real person — not professional relationship counseling or therapy. For serious or ongoing relationship difficulties, consider speaking with a licensed counselor.',
    faqs: [
      { q: 'Can I talk about my relationship without my partner knowing?', a: 'Yes. Conversations are anonymous and private — nothing you say is shared with anyone, including the person your conversation is about.' },
      { q: 'Will I get actual advice, or just someone to listen?', a: 'Both are welcome. You can ask directly for someone\'s honest take, or simply say you want to talk it through out loud without needing a solution.' },
      { q: 'Can I talk through a friendship problem too, not just romantic ones?', a: 'Yes — Baatcheet\'s relationship conversations cover romantic relationships, friendships, and family situations equally.' },
      { q: 'Is my conversation kept private?', a: 'Yes. Calls are end-to-end encrypted and never recorded or stored by Baatcheet — see our Safety page for the full breakdown.' },
    ],
    related: [
      { path: '/emotional-support', label: 'Emotional Support' },
      { path: '/talk-to-someone', label: 'Talk to Someone Right Now' },
      { path: '/safety', label: 'How Baatcheet keeps you safe' },
    ],
    ctaHeading: <>Get an <span className="stamp">outside view.</span></>,
    ctaBody: 'Download Baatcheet free and talk it through with someone who has no stake in the outcome.',
  },

  // Angle: in-the-moment pressure release — shorter, more acute than
  // mental-wellness's routine framing.
  'stress-relief': {
    slug: 'stress-relief',
    breadcrumbLabel: 'Stress Relief',
    title: 'Stress Relief — Vent It Out on a Free Voice Call',
    description: 'Stressed out? Talk it out. Baatcheet connects you to a real person for a quick, anonymous voice call whenever you need to unwind.',
    kicker: 'Ten minutes. Say it out loud.',
    h1: <>Let It Out.<br /><span className="stamp">Feel Lighter.</span></>,
    heroLead: 'Stress that stays in your head just loops. Saying it out loud — to someone who isn\'t your boss, your parent, or your group chat — tends to shrink it down to size. That\'s the entire job of Baatcheet in moments like this.',
    heroImage: { src: '/how-hero.webp', alt: 'A mood-matched voice call ready to start on Baatcheet', w: 1402, h: 1122, label: 'Quick call, real relief' },
    sections: [
      {
        heading: 'A pressure valve, not a project',
        paragraphs: [
          'You don\'t need a plan before you open Baatcheet — you need somewhere to put the stress for ten minutes. Pick "vent" as your mood, get matched fast, and just talk. No one\'s taking notes, no one\'s judging your tone, and no one expects you to have it figured out by the end of the call.',
        ],
        bullets: [
          'Matched in seconds — no waiting when stress is already high',
          'A "vent" mood built specifically for letting it out, unfiltered',
          'Short calls are completely normal — there\'s no minimum length',
        ],
      },
      {
        heading: 'Works on a 10-minute break',
        paragraphs: [
          'Between meetings, on a walk, waiting for the bus — Baatcheet is built for short windows, not long sit-down sessions. A quick call or even a voice note can be enough to reset before you go back to whatever\'s stressing you out.',
        ],
      },
    ],
    disclaimer: 'Baatcheet offers peer conversation to help you decompress in the moment — it isn\'t medical or psychological treatment. If stress is affecting you persistently, consider speaking with a healthcare professional.',
    faqs: [
      { q: 'How fast can I get on a call when I\'m stressed right now?', a: 'Baatcheet typically matches you with someone in under 8 seconds — there\'s no queue to wait through when you need to talk immediately.' },
      { q: 'Does venting out loud actually relieve stress?', a: 'Putting stress into spoken words, to a person who\'s actually listening, is a simple and well-known way to get some distance from it — that\'s the whole idea behind Baatcheet\'s "vent" mood.' },
      { q: 'Can I do this on a short break, like 10 minutes?', a: 'Yes. Calls have no minimum length — plenty of Baatcheet conversations last exactly as long as a coffee break.' },
      { q: 'What if I don\'t know what to say?', a: 'That\'s completely fine — you can just tell your match you\'re stressed and don\'t know where to start. Most conversations find their footing within the first minute.' },
    ],
    related: [
      { path: '/mental-wellness', label: 'Mental Wellness' },
      { path: '/talk-to-someone', label: 'Talk to Someone Right Now' },
      { path: '/download', label: 'Download Baatcheet' },
    ],
    ctaHeading: <>Let it <span className="stamp">out.</span></>,
    ctaBody: 'Download Baatcheet free and vent it out on a quick, anonymous voice call.',
  },

  // Angle: persistent state (feeling isolated) needing ongoing connection —
  // distinct from talk-to-someone's one-off moment framing.
  'loneliness-help': {
    slug: 'loneliness-help',
    breadcrumbLabel: 'Loneliness Help',
    title: 'Feeling Lonely? Talk to Real People on Baatcheet',
    description: 'Baatcheet helps you fight loneliness with real conversations, any time you need company, day or night. Free to start, always anonymous.',
    kicker: 'For the quiet hours.',
    h1: <>You're Never<br />Really <span className="stamp">Alone Here.</span></>,
    heroLead: 'New city, remote job, a schedule that doesn\'t match anyone else\'s, or just a stretch of life that feels quieter than usual — loneliness isn\'t always dramatic, it\'s often just an empty evening with no one to call. Baatcheet exists to fill exactly that gap.',
    heroImage: { src: '/join-hero.webp', alt: 'A warm, always-on Baatcheet community', w: 1536, h: 1024, label: 'Always someone around' },
    sections: [
      {
        heading: 'Company on tap, not a favor to ask',
        paragraphs: [
          'Asking a friend to "just talk" can feel like an imposition, especially at odd hours or on a bad week. On Baatcheet, that\'s literally what everyone is there for — no one\'s doing you a favor by picking up, because that\'s the entire point of the app.',
          'Whether it\'s a five-minute call before bed or an hour in a live room in the background while you cook dinner, the goal is the same: making the quiet stretches feel a little less empty.',
        ],
        bullets: [
          'Real people online at every hour, including the loneliest ones',
          'Live audio rooms if you want company without a 1-on-1 conversation',
          'No explanation needed — "just wanted company" is reason enough',
        ],
      },
      {
        heading: 'Build a regular connection, if you want one',
        paragraphs: [
          'Some people use Baatcheet for a single conversation whenever loneliness hits. Others find one or two people they genuinely click with and keep talking to them regularly. Both are completely normal ways to use it — there\'s no "correct" frequency.',
        ],
      },
    ],
    quote: { text: 'Naye shahar mein Baatcheet se dost mile — real waale.', attribution: 'A Baatcheet user, new to their city' },
    faqs: [
      { q: 'I\'ve just moved to a new city and don\'t know anyone — can this help?', a: 'Yes — this is one of the most common reasons people start using Baatcheet. It won\'t replace building an in-person circle over time, but it fills the gap of having someone to talk to while you do.' },
      { q: 'Can I talk to the same person regularly?', a: 'Yes. If you connect well with someone, you can choose to talk to them again — there\'s no rule requiring a new match every time.' },
      { q: 'Is it just one-off calls, or can I build an ongoing connection?', a: 'Both work. Baatcheet supports quick one-off conversations and ongoing ones equally — it\'s entirely up to what you and your match want.' },
      { q: 'What if it\'s the middle of the night and I feel alone?', a: 'Baatcheet has real people online around the clock — late night is actually one of the busiest times on the app, precisely because that\'s when loneliness tends to hit hardest.' },
    ],
    related: [
      { path: '/online-friends', label: 'Make Online Friends' },
      { path: '/talk-to-someone', label: 'Talk to Someone Right Now' },
      { path: '/audio-chat-app', label: 'Audio Chat App' },
    ],
    ctaHeading: <>You're never <span className="stamp">alone here.</span></>,
    ctaBody: 'Download Baatcheet free — there\'s always someone around to talk to.',
  },

  // Angle: activity/feature-first — building an ongoing friend circle, the
  // social-discovery counterpart to loneliness-help's emotional framing.
  'online-friends': {
    slug: 'online-friends',
    breadcrumbLabel: 'Online Friends',
    title: 'Make Online Friends — Real Voice Conversations',
    description: 'Meet new people and make real friends on Baatcheet through voice — no swiping, no fake profiles, just genuine conversation.',
    kicker: 'No swiping. Just talking.',
    h1: <>Make Friends Who<br /><span className="stamp">Actually Get You.</span></>,
    heroLead: 'Most "meet new people" apps run on photos and swiping, which tells you almost nothing about someone. Baatcheet runs on conversation first — you get to know how someone talks and thinks before you ever see what they look like, if you ever do.',
    heroImage: { src: '/partner-illus.webp', alt: 'A community of real people to meet and befriend on Baatcheet', w: 1536, h: 1024, label: 'Real people, real talk' },
    sections: [
      {
        heading: 'Conversation first, everything else later',
        paragraphs: [
          'Baatcheet matches you by mood and interest, not by photo. That means your first impression of someone is entirely how they sound and what they say — a much better predictor of an actual friendship than a curated profile picture ever was.',
          'From there, it works like any real friendship: you talk once, and if it clicks, you talk again. No follower counts, no algorithm deciding who you should be friends with.',
        ],
        bullets: [
          'Matched by mood and interest, not appearance',
          'Live audio rooms to meet people with shared interests in a group setting',
          'Every account is identity-verified — no bots, no fake profiles',
        ],
      },
      {
        heading: 'From a single call to an actual friendship',
        paragraphs: [
          'Plenty of Baatcheet\'s longest-running friendships started as a single anonymous call that neither person expected to repeat. If a conversation clicks, keep talking to that person — there\'s no feature standing between "one nice call" and "a friend you talk to every week."',
        ],
      },
    ],
    quote: { text: 'Yeh app ghar jaisa lagta hai — apni language mein, apne log.', attribution: 'A Baatcheet user' },
    faqs: [
      { q: 'How is this different from social media?', a: 'Social media is built around broadcasting to a following. Baatcheet is built around one conversation at a time — you\'re talking with someone, not posting for an audience.' },
      { q: 'Can I stay in touch with someone after the first call?', a: 'Yes. If a conversation goes well, you can choose to talk to that same person again — nothing about the app pushes you toward always matching with someone new.' },
      { q: 'Are the people on Baatcheet verified/real?', a: 'Yes — every account passes identity verification before it can chat with anyone, which is how Baatcheet keeps bots and fake profiles out.' },
      { q: 'Can I make friends without going on video?', a: 'Yes. Baatcheet is voice-first — video is never required to have a full conversation or build a friendship.' },
    ],
    related: [
      { path: '/audio-chat-app', label: 'Audio Chat App' },
      { path: '/loneliness-help', label: 'Loneliness Help' },
      { path: '/join-us', label: 'Join Baatcheet' },
    ],
    ctaHeading: <>Make a <span className="stamp">real friend.</span></>,
    ctaBody: 'Download Baatcheet free and meet someone worth talking to twice.',
  },

  // Angle: voice gig economy — earning by talking. Distinct from the product
  // pages (/join-us, /partner-landing): this targets search demand ("get paid
  // to talk online") that no existing page captures. Transactional intent.
  'earn-money-talking-online': {
    slug: 'earn-money-talking-online',
    breadcrumbLabel: 'Earn Money Talking',
    title: 'Earn Money Talking to People Online | Baatcheet',
    description: 'Get paid to talk to people online in India. Set your own hours, verify once, and earn per minute as a Baatcheet voice partner. Free to apply.',
    kicker: 'Your voice. Your hours. Your income.',
    h1: <>Get Paid to<br /><span className="stamp">Talk.</span></>,
    heroLead: "Some people are just good at conversation — at listening, at making someone feel heard. Baatcheet turns that into real income. Set your own hours, get verified once, and earn per minute doing something you'd do anyway: talk.",
    heroImage: { src: '/partner-illus.webp', alt: 'Baatcheet voice partners earning by talking', w: 1536, h: 1024, label: 'Weekly payouts' },
    sections: [
      {
        heading: 'A real side income, not a get-rich scheme',
        paragraphs: [
          'Let\'s be honest upfront: this isn\'t instant money, and anyone promising a fixed guaranteed figure isn\'t being straight with you. What it is: a genuine, flexible side income that grows with the hours you put in — earning per-minute on voice conversations with people who genuinely want someone to talk to.',
          'For students, homemakers, and anyone wanting income on their own schedule, it\'s one of the few options where the only real requirement is being a warm, attentive human being.',
        ],
        bullets: [
          'Earn per minute on voice calls — the more you talk, the more you earn',
          'Set your own hours — no fixed shifts, work when you want',
          'Weekly payouts straight to your bank or UPI',
        ],
      },
      {
        heading: 'Verified once, safe throughout',
        paragraphs: [
          'Before you take your first call, you go through a one-time identity verification (government ID plus a live check). This protects the people you talk to — and it means you\'re part of a platform where everyone else is verified too. You\'ll never be asked to pay anything to start; a legitimate platform verifies you, it doesn\'t charge you.',
        ],
      },
    ],
    disclaimer: 'Earnings vary based on hours, timing, and demand — Baatcheet publishes its actual rate structure rather than promising a fixed figure. Treat this as a flexible side income that grows with effort, not a guaranteed salary.',
    faqs: [
      { q: 'Is it really possible to earn money just by talking?', a: 'Yes — on verified platforms that pay per-minute for voice conversations. It\'s a genuine side income, though earnings vary with the hours you put in.' },
      { q: 'Do I have to pay anything to start earning?', a: 'No. A legitimate platform verifies your identity as a safety step but never charges you to begin — any upfront "registration" or "training" fee is a scam signal.' },
      { q: 'How and when do I get paid?', a: 'Baatcheet partners are paid on a weekly cycle, straight to a bank account or UPI. Exact per-minute rates are published in the partner program.' },
      { q: 'How many hours do I need to put in?', a: 'It\'s fully flexible — you set your own hours. More hours (especially during high-demand times) means more earnings, but there\'s no minimum requirement.' },
    ],
    related: [
      { path: '/join-us', label: 'Join the Partner Program' },
      { path: '/partner-landing', label: 'Partner Program Details' },
      { path: '/how-it-works', label: 'How Baatcheet works' },
    ],
    ctaHeading: <>Start <span className="stamp">earning.</span></>,
    ctaBody: 'Apply to become a Baatcheet voice partner — verification is quick and free.',
  },

  // Angle: the late-night state — insomnia, 2am overthinking, needing company
  // when everyone's asleep. Distinct from /talk-to-someone (a general moment)
  // and /loneliness-help (an ongoing state): this is specifically about night.
  'late-night-chat-app': {
    slug: 'late-night-chat-app',
    breadcrumbLabel: 'Late Night Chat App',
    title: 'Late Night Chat App — Someone to Talk To at 2AM | Baatcheet',
    description: 'Can\'t sleep? Baatcheet connects you to real people awake right now — for late-night talks, 2am overthinking, or just company. Free, anonymous.',
    kicker: 'For the quiet, sleepless hours.',
    h1: <>Someone's Awake<br /><span className="stamp">Right Now.</span></>,
    heroLead: 'The same thought that felt fine at 3pm can feel unbearable at 3am. When you can\'t sleep and can\'t stop thinking — and texting a friend feels impossible at that hour — Baatcheet connects you to a real person who\'s also awake, in seconds.',
    heroImage: { src: '/how-app-dark.webp', alt: 'Baatcheet\'s late-night mood-matched call screen', w: 1349, h: 1166, label: 'Awake · 24/7' },
    sections: [
      {
        heading: 'Why late night hits different',
        paragraphs: [
          'Nighttime amplifies everything — your body\'s rhythm shifts, distractions disappear, and a spiraling thought gets the whole quiet room to itself. It\'s a near-universal pattern, which is exactly why "2am thoughts" is such a recognized phrase. The problem isn\'t you; it\'s the hour.',
          'And late night is precisely when reaching out feels hardest: you don\'t want to wake a friend, seem dramatic, or explain it in the morning. That hesitation is the exact gap a late-night chat fills.',
        ],
        bullets: [
          'Real people online around the clock — late night is the busiest time',
          'No waking anyone up, no guilt, no explaining in the morning',
          'Anonymous by default — say what\'s actually keeping you up',
        ],
      },
      {
        heading: 'Talking as a wind-down, not a wake-up',
        paragraphs: [
          'A calm late-night conversation tends to settle racing thoughts rather than energize them — moving what\'s in your head into words is often what finally lets you sleep, instead of lying there running the same loop. You\'re also genuinely not the only one awake: there\'s a real late-night crowd, up and looking for the same thing you are.',
        ],
      },
    ],
    disclaimer: 'Baatcheet is real peer conversation, not a crisis line or a substitute for professional care. If sleeplessness is severe or persistent, or you\'re in crisis, please reach out to a doctor or a helpline.',
    faqs: [
      { q: 'Is anyone actually available to talk at 2 or 3am?', a: 'Yes — late night is one of the busiest times on Baatcheet, precisely because that\'s when this need is highest.' },
      { q: 'Won\'t talking to someone keep me awake longer?', a: 'Usually the opposite — a calm conversation tends to settle racing thoughts by getting them out of your head, which often helps you sleep.' },
      { q: 'Do I have to explain why I\'m up?', a: 'No — you can just say you couldn\'t sleep and wanted to talk. That\'s a complete reason on its own.' },
    ],
    related: [
      { path: '/talk-to-someone', label: 'Talk to Someone Now' },
      { path: '/loneliness-help', label: 'Loneliness Help' },
      { path: '/emotional-support', label: 'Emotional Support' },
    ],
    ctaHeading: <>Can't sleep? <span className="stamp">Talk.</span></>,
    ctaBody: 'Download Baatcheet free — someone\'s awake and ready to talk right now.',
  },

  // Angle: vernacular / desi voice culture — Hindi & regional language, NRI.
  // The biggest differentiator vs global English-only competitors. Distinct
  // from /voice-chat-app (format) by being about language and cultural fit.
  'hindi-chat-app': {
    slug: 'hindi-chat-app',
    breadcrumbLabel: 'Hindi Chat App',
    title: 'Hindi Chat App — Talk in Your Own Language | Baatcheet',
    description: 'Talk to real people in Hindi and regional languages on Baatcheet — voice-first, anonymous, made for India. Bol, jaise tu asli mein baat karta hai.',
    kicker: 'Apni zabaan mein baat karo.',
    h1: <>Talk in the<br /><span className="stamp">Language You Think In.</span></>,
    heroLead: 'Typing in Hindi is friction. Switching between Hindi and English mid-sentence — the way you actually talk — is even more. Baatcheet is voice-first for exactly this reason: just speak, in whatever mix of languages home sounds like, to someone who talks the same way.',
    heroImage: { src: '/how-hero.webp', alt: 'Talking in Hindi and regional languages on Baatcheet', w: 1402, h: 1122, label: 'Made in Bharat' },
    sections: [
      {
        heading: 'Voice removes the language friction',
        paragraphs: [
          'English keyboards were never built for how India actually speaks. Typing fluently in Hindi, or code-switching between a regional language and English the way everyday conversation does, is real friction that voice simply doesn\'t have. Saying a sentence in your natural language mix is effortless; typing that same sentence often isn\'t.',
          'That\'s a big part of why voice notes became India\'s default — and why a voice-first app feels natural in a way a text-first one never quite does here.',
        ],
        bullets: [
          'Talk in Hindi, Hinglish, or your regional language — no typing',
          'Matched with people who speak the way you do',
          'Anonymous and verified — real desi log, no fake profiles',
        ],
      },
      {
        heading: 'For everyone missing home',
        paragraphs: [
          'For NRIs and students abroad especially, there\'s a specific comfort in talking in your mother tongue with someone who shares the context — no translating, no explaining why a festival matters, just an easy conversation in the language you feel in. It\'s a small, real piece of home, available whenever you need it.',
        ],
      },
    ],
    faqs: [
      { q: 'Can I talk in Hindi instead of typing?', a: 'Yes — Baatcheet is voice-first, so you just speak in Hindi, Hinglish, or your regional language. No typing in a second-nature-unfriendly keyboard required.' },
      { q: 'Will I be matched with people who speak my language?', a: 'Baatcheet\'s matching accounts for language and mood, so you\'re connected with people who talk the way you do.' },
      { q: 'Does it work for regional languages beyond Hindi?', a: 'Yes — the voice-first approach works for any language you speak, which is the whole point: talk the way you actually talk.' },
    ],
    related: [
      { path: '/voice-chat-app', label: 'Voice Chat App' },
      { path: '/online-friends', label: 'Make Online Friends' },
      { path: '/audio-chat-app', label: 'Audio Chat App' },
    ],
    ctaHeading: <>Bol, <span className="stamp">apni zabaan mein.</span></>,
    ctaBody: 'Download Baatcheet free and talk in the language you actually think in.',
  },
};

export default landingData;
